import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface MiscorrectedConnection {
  id: string;
  source: 'scheduled' | 'ring';
  event_date: string;
  contact_id: string | null;
  contact_name: string | null;
  scheduled_by: string | null;
  scheduled_by_name: string | null;
  confirmed_by: string | null;
  confirmed_by_name: string | null;
  connection_confirmed_at: string | null;
  connection_notes: string | null;
}

interface ConnectionFilters {
  startDate?: string;
  endDate?: string;
  sdrId?: string;
  contactSearch?: string;
}

export function useConnectionCorrections() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ConnectionFilters>({});

  const fetchMiscorrectedConnections = useCallback(async (): Promise<MiscorrectedConnection[]> => {
    const results: MiscorrectedConnection[] = [];

    // 1. Fetch from calls (scheduled video meetings)
    let callsQuery = supabase
      .from('calls')
      .select(`
        id,
        completed_at,
        contact_id,
        scheduled_by,
        connection_confirmed_by,
        connection_confirmed_at,
        connection_notes,
        contacts:contacts!contact_calls_contact_id_fkey(first_name, last_name),
        sdr:app_users!calls_scheduled_by_fkey(display_name),
        confirmer:app_users!calls_connection_confirmed_by_fkey(display_name)
      `)
      .eq('channel', 'video')
      .eq('status', 'completed')
      .eq('connection_confirmed', false)
      .not('scheduled_by', 'is', null)
      .order('completed_at', { ascending: false })
      .limit(100);

    if (filters.sdrId) {
      callsQuery = callsQuery.eq('scheduled_by', filters.sdrId);
    }
    if (filters.startDate) {
      callsQuery = callsQuery.gte('completed_at', filters.startDate);
    }
    if (filters.endDate) {
      callsQuery = callsQuery.lte('completed_at', filters.endDate + 'T23:59:59');
    }

    const { data: callsData, error: callsError } = await callsQuery;
    if (callsError) throw callsError;

    for (const call of callsData || []) {
      const contact = call.contacts as { first_name: string | null; last_name: string | null } | null;
      const sdr = call.sdr as { display_name: string | null } | null;
      const confirmer = call.confirmer as { display_name: string | null } | null;
      
      const contactName = contact 
        ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') 
        : null;

      // Apply contact search filter
      if (filters.contactSearch && contactName) {
        if (!contactName.toLowerCase().includes(filters.contactSearch.toLowerCase())) {
          continue;
        }
      }

      results.push({
        id: call.id,
        source: 'scheduled',
        event_date: call.completed_at,
        contact_id: call.contact_id,
        contact_name: contactName,
        scheduled_by: call.scheduled_by,
        scheduled_by_name: sdr?.display_name || null,
        confirmed_by: call.connection_confirmed_by,
        confirmed_by_name: confirmer?.display_name || null,
        connection_confirmed_at: call.connection_confirmed_at,
        connection_notes: call.connection_notes,
      });
    }

    // 2. Fetch from video_call_ring_requests (Video Now / instant calls)
    let ringQuery = supabase
      .from('video_call_ring_requests')
      .select(`
        id,
        accepted_at,
        contact_id,
        requested_by,
        connection_confirmed_by,
        connection_confirmed_at,
        connection_notes,
        contacts(first_name, last_name),
        requester:app_users!video_call_ring_requests_requested_by_fkey(display_name),
        confirmer:app_users!video_call_ring_requests_connection_confirmed_by_fkey(display_name)
      `)
      .eq('status', 'accepted')
      .eq('connection_confirmed', false)
      .not('requested_by', 'is', null)
      .order('accepted_at', { ascending: false })
      .limit(100);

    if (filters.sdrId) {
      ringQuery = ringQuery.eq('requested_by', filters.sdrId);
    }
    if (filters.startDate) {
      ringQuery = ringQuery.gte('accepted_at', filters.startDate);
    }
    if (filters.endDate) {
      ringQuery = ringQuery.lte('accepted_at', filters.endDate + 'T23:59:59');
    }

    const { data: ringData, error: ringError } = await ringQuery;
    if (ringError) throw ringError;

    for (const ring of ringData || []) {
      const contact = ring.contacts as { first_name: string | null; last_name: string | null } | null;
      const requester = ring.requester as { display_name: string | null } | null;
      const confirmer = ring.confirmer as { display_name: string | null } | null;

      const contactName = contact 
        ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') 
        : null;

      // Apply contact search filter
      if (filters.contactSearch && contactName) {
        if (!contactName.toLowerCase().includes(filters.contactSearch.toLowerCase())) {
          continue;
        }
      }

      results.push({
        id: ring.id,
        source: 'ring',
        event_date: ring.accepted_at,
        contact_id: ring.contact_id,
        contact_name: contactName,
        scheduled_by: ring.requested_by,
        scheduled_by_name: requester?.display_name || null,
        confirmed_by: ring.connection_confirmed_by,
        confirmed_by_name: confirmer?.display_name || null,
        connection_confirmed_at: ring.connection_confirmed_at,
        connection_notes: ring.connection_notes,
      });
    }

    // Sort all results by event_date descending
    results.sort((a, b) => {
      const dateA = new Date(a.event_date || 0).getTime();
      const dateB = new Date(b.event_date || 0).getTime();
      return dateB - dateA;
    });

    return results;
  }, [filters]);

  const query = useQuery({
    queryKey: ['miscorrected-connections', filters],
    queryFn: fetchMiscorrectedConnections,
  });

  const correctConnectionMutation = useMutation({
    mutationFn: async ({ id, source, notes }: { id: string; source: 'scheduled' | 'ring'; notes?: string }) => {
      const correctionNote = notes 
        ? `Correção manual: ${notes}` 
        : 'Correção manual: conexão confirmada retroativamente';

      if (source === 'scheduled') {
        const { error } = await supabase
          .from('calls')
          .update({
            connection_confirmed: true,
            connection_notes: correctionNote,
          })
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('video_call_ring_requests')
          .update({
            connection_confirmed: true,
            connection_notes: correctionNote,
          })
          .eq('id', id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('Conexão corrigida com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['miscorrected-connections'] });
    },
    onError: (error) => {
      console.error('Error correcting connection:', error);
      toast.error('Erro ao corrigir conexão');
    },
  });

  return {
    connections: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    filters,
    setFilters,
    correctConnection: correctConnectionMutation.mutate,
    isCorrectingConnection: correctConnectionMutation.isPending,
    refetch: query.refetch,
  };
}
